import {
  InboxItemAssignee,
  OnboardingTask,
  PendingInboxItem,
} from "@forgepoint/shared";
import type { InboxTaskType } from "@/pages/inboxFilterTypes";
import {
  compareInboxSortKeys,
  type InboxSort,
  type InboxSortKeys,
} from "@/pages/inboxTableSort";

/** One row of the task inbox table: an agent task, or an open onboarding task. */
export type InboxTableRow =
  | { kind: "agent"; item: PendingInboxItem }
  | {
      kind: "onboarding";
      task: OnboardingTask;
      assignees: InboxItemAssignee[];
    };

/** Everything the inbox table's rows are built from. */
export interface InboxTableRowsInput {
  pendingItems: PendingInboxItem[];
  completedItems: PendingInboxItem[];
  onboardingTasks: OnboardingTask[];
  showCompleted: boolean;
  sort: InboxSort;
  taskType: InboxTaskType | null;
  assigneeNameFor: (userId: string) => string;
}

/**
 * Builds the inbox table's rows: open agent and onboarding tasks sorted
 * together, then completed agent tasks when shown, in the same order. A task
 * type filter keeps only the rows of that kind.
 */
export function buildInboxTableRows(
  input: InboxTableRowsInput,
): InboxTableRow[] {
  const openRows = [
    ...input.pendingItems.map(toAgentRow),
    ...input.onboardingTasks.map((task) =>
      toOnboardingRow(task, input.assigneeNameFor),
    ),
  ];
  const completedRows = input.showCompleted
    ? input.completedItems.map(toAgentRow)
    : [];
  const rows = [
    ...sortRows(openRows, input.sort),
    ...sortRows(completedRows, input.sort),
  ];
  if (!input.taskType) {
    return rows;
  }
  return rows.filter((row) => row.kind === input.taskType);
}

/** Reads when a row's task last changed. */
export function getRowUpdatedAt(row: InboxTableRow): Date {
  return row.kind === "agent" ? row.item.updatedAt : row.task.updatedAt;
}

function toAgentRow(item: PendingInboxItem): InboxTableRow {
  return { kind: "agent", item };
}

function toOnboardingRow(
  task: OnboardingTask,
  assigneeNameFor: (userId: string) => string,
): InboxTableRow {
  const assignees = task.assigneeUserId
    ? [
        {
          userId: task.assigneeUserId,
          name: assigneeNameFor(task.assigneeUserId),
        },
      ]
    : [];
  return { kind: "onboarding", task, assignees };
}

function sortRows(rows: InboxTableRow[], sort: InboxSort): InboxTableRow[] {
  return [...rows].sort((a, b) =>
    compareInboxSortKeys(getRowSortKeys(a), getRowSortKeys(b), sort),
  );
}

function getRowSortKeys(row: InboxTableRow): InboxSortKeys {
  return {
    opportunityName: row.kind === "agent" ? row.item.opportunityName : null,
    updatedAt: getRowUpdatedAt(row),
  };
}
